import { useFormik } from 'formik'
import { useMemo } from 'react'
import { Form, FormLabel, Spinner } from 'react-bootstrap'
import { RiCloseLine } from 'react-icons/ri'
import * as Yup from 'yup'
import CustomDropzone from '../CustomDropzone.jsx'
import { useUploadEegFilesMutation } from '../../redux/api/pipelineApi.js'

// Allowed extensions
const allowedExtensions = ['edf', 'csv', 'set']

// Map known types to dropzone format
const typeMap = {
  csv: { 'text/csv': ['.csv'] },
  set: { 'application/octet-stream': ['.set'] },
  edf: { 'application/octet-stream': ['.edf'] },
}

// Validation Schema
const validationSchema = Yup.object().shape({
  fileType: Yup.string().oneOf(allowedExtensions, 'Invalid file type').required('Please select a file type'),
  files: Yup.array()
    .min(1, 'At least one file is required')
    .test('all-same-type', 'All files must match the selected file type', function (files) {
      const { fileType } = this.parent
      if (!fileType || !files) return true
      return files.every((file) => file.name.toLowerCase().endsWith(`.${fileType}`))
    }),
})

const UploadEegFiles = ({ setStep }) => {
  const [uploadEegFiles, { isLoading }] = useUploadEegFilesMutation()

  const handleSubmit = async (values, { setFieldError, resetForm }) => {
    try {
      const formData = new FormData()
      values.files.forEach((file) => formData.append('eeg', file))

      await uploadEegFiles({ formData }).unwrap()

      resetForm()

      setStep((s) => s + 1)
    } catch (err) {
      setFieldError('form', err?.data?.error || err?.data?.message || 'An error occurred during upload')
    }
  }

  const formik = useFormik({
    initialValues: { files: [], fileType: '' },
    validationSchema,
    onSubmit: handleSubmit,
  })

  // Pick the accepted config based on fileType state
  const acceptedFileTypes = useMemo(() => typeMap[formik.values.fileType] || {}, [formik.values.fileType])

  return (
    <Form onSubmit={formik.handleSubmit}>
      {/* File Type Selector */}
      <Form.Group className='mb-3'>
        <FormLabel>File Type</FormLabel>
        <Form.Select name='fileType' value={formik.values.fileType} onChange={formik.handleChange} onBlur={formik.handleBlur}>
          <option value=''>Select a type</option>
          {allowedExtensions.map((ext) => (
            <option key={ext} value={ext}>
              {ext.toUpperCase()}
            </option>
          ))}
        </Form.Select>
        {formik.touched.fileType && formik.errors.fileType && (
          <div className='text-danger mt-1' style={{ fontSize: '0.875rem' }}>
            {formik.errors.fileType}
          </div>
        )}
      </Form.Group>

      {/* File Upload */}
      <Form.Group>
        <FormLabel>Files</FormLabel>
        <CustomDropzone
          acceptedFileTypes={acceptedFileTypes}
          isMultiple={true}
          onSubmit={(newFiles) => {
            const mergedFiles = [...formik.values.files, ...newFiles]
            formik.setFieldValue('files', mergedFiles)
            formik.setFieldTouched('files', true, false)
            formik.setErrors({ ...formik.errors, files: undefined }) // clear files error
          }}
          loading={isLoading}
        />
        {formik.touched.files && formik.errors.files && (
          <div className='text-danger mt-1' style={{ fontSize: '0.875rem' }}>
            {formik.errors.files}
          </div>
        )}
      </Form.Group>

      {/* Display uploaded files */}
      {formik.values.files.map((file, idx) => (
        <FileDisplay
          key={idx}
          file={file}
          onRemove={() =>
            formik.setFieldValue(
              'files',
              formik.values.files.filter((_, i) => i !== idx)
            )
          }
          disabled={isLoading}
        />
      ))}

      {formik.errors.form && <div className='text-danger mb-3'>{formik.errors.form}</div>}

      <div className='mt-2 d-flex align-item-center gap-2'>
        <button type='button' className='common-btn py-2 flex-grow-1' onClick={() => setStep((s) => s - 1)} disabled={isLoading}>
          Back
        </button>

        {/* Submit */}
        <button type='submit' className='common-btn py-2 flex-grow-1' disabled={isLoading}>
          {isLoading ? (
            <div className='d-flex align-items-center justify-content-center gap-2'>
              <Spinner animation='border' size='sm' />
              Uploading...
            </div>
          ) : (
            'Upload'
          )}
        </button>
      </div>
    </Form>
  )
}

export default UploadEegFiles

const FileDisplay = ({ file, onRemove, disabled }) => (
  <div className='d-flex justify-content-between align-items-center p-3 rounded-3 my-2' style={{ backgroundColor: '#f6f6f6' }}>
    <div>
      <p className='fw-bold m-0' style={{ wordBreak: 'break-word' }}>
        {file.name}
      </p>
      <p className='text-secondary m-0'>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
    </div>
    <button type='button' className='border-0 rounded-circle bg-transparent' onClick={onRemove} disabled={disabled}>
      <RiCloseLine size={24} />
    </button>
  </div>
)
